import { Piano } from "../../piano";
import { ComputerKeyboardInput } from "../keyboard";
import {
  Observable,
  type Disposable,
  type ReadonlyObservable,
} from "@/lib/studio/observable";

const ENABLED_LS_KEY = "virtuosa.keyboardEnabled";

function readEnabled(): boolean {
  if (typeof window === "undefined") {
    return true;
  }

  try {
    const v = window.localStorage.getItem(ENABLED_LS_KEY);

    return v === null ? true : v === "1";
  } catch {
    return true;
  }
}

function writeEnabled(enabled: boolean): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(ENABLED_LS_KEY, enabled ? "1" : "0");
  } catch {
    // Ignore quota / privacy errors.
  }
}

/**
 * Wraps the computer-keyboard piano input. Two independent signals drive the
 * effective listener attachment:
 *
 *  - `enabled` is the user preference, persisted to localStorage and toggled
 *    from the studio settings overlay.
 *  - `scoped` is owned by the piano viewport (true while it is mounted), so
 *    keys outside the studio screens never feed the piano.
 *
 * Listeners are wired iff both flags are true.
 */
export class KeyboardController implements Disposable {
  private input: ComputerKeyboardInput;
  private _active = new Observable<boolean>(false);
  private _enabled = new Observable<boolean>(readEnabled());
  private scoped = false;

  constructor(piano: Piano) {
    this.input = new ComputerKeyboardInput(piano);
  }

  get active(): ReadonlyObservable<boolean> {
    return this._active;
  }

  get enabled(): ReadonlyObservable<boolean> {
    return this._enabled;
  }

  setScoped(scoped: boolean): void {
    if (this.scoped === scoped) {
      return;
    }

    this.scoped = scoped;
    this.refresh();
  }

  setEnabled(enabled: boolean): void {
    if (this._enabled.value === enabled) {
      return;
    }

    this._enabled.set(enabled);
    writeEnabled(enabled);
    this.refresh();
  }

  private refresh(): void {
    const should = this.scoped && this._enabled.value;

    if (should === this._active.value) {
      return;
    }

    if (should) {
      this.input.attach();
    } else {
      this.input.detach();
    }

    this._active.set(should);
  }

  dispose(): void {
    this.scoped = false;
    this.refresh();
    this._active.dispose();
    this._enabled.dispose();
  }
}
